import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Home, X, Plus } from 'lucide-react';
import Flag from './Flag';
import CountryProfile from './CountryProfile';
import { useJazyk } from '../../context/LanguageContext';

/**
 * Co je v zemi, na kterou se kliklo na mapě.
 *
 * Panel se ukazuje **pod mapou**, ne místo ní. Klik na mapu je zvědavost, ne
 * rozhodnutí někam odejít — když člověk zkoumá, kde co je, nechce po každém
 * kliknutí přijít o mapu a vracet se tlačítkem zpět.
 *
 * Odchod na stránku země je proto samostatné tlačítko, ne vedlejší účinek
 * kliknutí do mapy.
 */

const SEKCE = ['specialists', 'organizations', 'events', 'pageants', 'resources'];

export default function CountryPreview({ zeme, nacita, onZavrit, onNastavitDomov, jeDomov }) {
  const { t, dvojjazycne } = useJazyk();

  if (nacita) {
    return (
      <div className="mt-4 rounded-3xl border border-border/40 bg-white p-8 flex justify-center"
           data-testid="nahled-zeme-nacita">
        <div className="spinner" />
      </div>
    );
  }
  if (!zeme) return null;

  const nazev = dvojjazycne(zeme.name_cs, zeme.name_en);
  const naplnene = SEKCE.filter((k) => (zeme.counts?.[k] || 0) > 0);

  return (
    <section className="mt-4 rounded-3xl border border-bloom-violet/25 bg-white shadow-sm overflow-hidden"
             data-testid="nahled-zeme">
      <div className="bg-bloom-violet/5 px-5 py-4 flex items-start gap-3 flex-wrap">
        <Flag emoji={zeme.flag} iso2={zeme.iso2} size="lg" />
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-xl font-extrabold text-bloom-text">{nazev}</h2>
          <p className="text-sm text-bloom-sub">
            {zeme.total > 0
              ? t('vypis.zaznamu', { pocet: zeme.total })
              : t('nahled.jenInformace')}
          </p>
        </div>
        <button type="button" onClick={onZavrit} aria-label={t('nahled.zavrit')}
                className="p-1.5 rounded-lg text-bloom-sub hover:text-bloom-text hover:bg-bloom-violet/10"
                data-testid="nahled-zavrit">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-5 pb-5">
        {/* Praktické údaje. Když je země nemá, komponenta se sama neukáže. */}
        <CountryProfile zeme={zeme} />

        {/* Co v zemi je, po druzích – ať je vidět, jestli má smysl jít dál. */}
        {naplnene.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-1.5">
            {naplnene.map((k) => (
              <Link key={k} to={`/world/${zeme.slug}/${k}`}
                    className="text-xs bg-bloom-bg border border-border/50 rounded-lg px-2.5 py-1.5 text-bloom-sub hover:text-bloom-violet hover:border-bloom-violet/30">
                {t(`sekce.${k}`)} <span className="opacity-70">{zeme.counts[k]}</span>
              </Link>
            ))}
          </div>
        )}

        {zeme.total === 0 && (
          <p className="mt-5 text-sm text-bloom-sub">{t('nahled.zatimNic')}</p>
        )}

        <div className="mt-5 flex flex-wrap gap-2">
          <Link to={`/world/${zeme.slug}`}
                className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-bloom-violet hover:bg-bloom-violet/90 px-4 py-2.5 rounded-xl"
                data-testid="nahled-otevrit">
            {t('nahled.otevrit', { zeme: nazev })} <ArrowRight className="w-4 h-4" />
          </Link>

          {zeme.total === 0 && (
            <Link to={`/world/${zeme.slug}`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-bloom-violet bg-white border border-bloom-violet/25 px-4 py-2.5 rounded-xl hover:bg-bloom-violet/5">
              <Plus className="w-4 h-4" /> {t('zeme.navrhnout')}
            </Link>
          )}

          {jeDomov ? (
            <span className="inline-flex items-center gap-1.5 text-sm bg-bloom-mint/15 border border-bloom-mint/40 rounded-xl px-3 py-2.5 text-bloom-text">
              <Home className="w-4 h-4 text-bloom-violet" /> {t('zeme.domovska')}
            </span>
          ) : (
            <button type="button" onClick={() => onNastavitDomov(zeme.iso2)}
                    className="inline-flex items-center gap-1.5 text-sm bg-white border border-border/50 text-bloom-sub rounded-xl px-3 py-2.5 hover:text-bloom-violet hover:border-bloom-violet/30">
              <Home className="w-4 h-4" /> {t('zeme.nastavitDomovskou')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
